import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  ShoppingCart,
  Package,
  CheckCircle,
  X,
  ChevronLeft,
  ChevronRight,
  Truck,
  Shield,
  Phone,
  Plus,
  Minus,
  Star,
  MessageSquare,
  ZoomIn,
  Ruler,
  AlertTriangle,
  Share2
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { toast } from "sonner";
import LazyImage from "../ui/LazyImage";
import { useCompanySettings } from "../utils/CompanySettings";
import { getBestApplicableCoupon } from "./shopUtils";
import { Tag } from "lucide-react";
import KlarnaWidget from "./KlarnaWidget";

export default function ProductDetailModal({ product, allProducts = [], isOpen, onClose, onAddToCart, onUpdateCartItem, cart, brand, activeCoupons = [], onViewDetails }) {
  const company = useCompanySettings();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedSide, setSelectedSide] = useState(null);
  const [selectedToeCap, setSelectedToeCap] = useState(null);
  const [selectedHeight, setSelectedHeight] = useState(null);
  const [selectedCompressionClass, setSelectedCompressionClass] = useState(null);
  const [sizeConfirmed, setSizeConfirmed] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState("");
  const [isZoomed, setIsZoomed] = useState(false);
  const [isGuideZoomed, setIsGuideZoomed] = useState(false);
  const [customMeasurements, setCustomMeasurements] = useState({});
  const [measurementErrors, setMeasurementErrors] = useState({});
  const queryClient = useQueryClient();

  // Reset selections when product changes
  useEffect(() => {
    if (product?.id) {
      setSelectedColor(null);
      setSelectedSize(null);
      setSelectedSide(null);
      setSelectedToeCap(null);
      setSelectedHeight(null);
      setSelectedCompressionClass(null);
      setQuantity(1);
      setCurrentImageIndex(0);
      setShowReviewForm(false);
      setReviewRating(5);
      setReviewComment("");
      setIsZoomed(false);
      setIsGuideZoomed(false);
      setSizeConfirmed(false);
      setCustomMeasurements({});
      setMeasurementErrors({});
    }
  }, [product?.id]);

  const { data: reviews = [] } = useQuery({
    queryKey: ['product-reviews', product?.id],
    queryFn: () => base44.entities.ProductReview.filter({ product_id: product.id, status: 'approved' }),
    enabled: !!product?.id,
  });

  const submitReviewMutation = useMutation({
    mutationFn: async (data) => {
      const res = await base44.functions.invoke('submitProductReview', data);
      if (res.data.error) throw new Error(res.data.error);
      return res.data;
    },
    onSuccess: () => {
      toast.success("Avaliação enviada! Aguarda aprovação.");
      setShowReviewForm(false);
      setReviewComment("");
    },
    onError: (err) => {
      toast.error(err.message);
    }
  });

  const handleSubmitReview = (e) => {
    e.preventDefault();
    submitReviewMutation.mutate({
      product_id: product.id,
      rating: reviewRating,
      comment: reviewComment
    });
  };

  const hasVariants = product?.has_variants && product?.variant_type !== 'none';
  const hasColorVariants = hasVariants && (product.variant_type === 'color' || product.variant_type === 'color_size' || product.variant_type === 'color_side' || product.variant_type === 'color_size_side' || product.variant_type === 'compression_size_height_toe_cap_color');
  const hasSizeVariants = hasVariants && (product.variant_type === 'size' || product.variant_type === 'color_size' || product.variant_type === 'size_side' || product.variant_type === 'color_size_side' || product.variant_type === 'compression_size_height_toe_cap_color');
  const hasSideVariants = hasVariants && (product.variant_type === 'side' || product.variant_type === 'size_side' || product.variant_type === 'color_side' || product.variant_type === 'color_size_side');
  const hasToeCapVariants = hasVariants && (product.variant_type === 'toe_cap' || product.variant_type === 'compression_size_height_toe_cap_color');
  const hasHeightVariants = hasVariants && (product.variant_type === 'height' || product.variant_type === 'compression_size_height_toe_cap_color');
  const hasCompressionVariants = hasVariants && (product.variant_type === 'compression' || product.variant_type === 'compression_size_height_toe_cap_color');

  // Extract available_sides from variants if not explicitly defined
  const availableSides = product?.available_sides?.length > 0 
    ? product.available_sides 
    : hasSideVariants && product?.variants?.length > 0
      ? [...new Set(product.variants.map(v => v.side).filter(Boolean))]
      : [];

  const availableToeCaps = product?.available_toe_caps?.length > 0
    ? product.available_toe_caps
    : hasToeCapVariants && product?.variants?.length > 0
      ? [...new Set(product.variants.map(v => v.toe_cap).filter(Boolean))]
      : [];

  const availableHeights = product?.available_heights?.length > 0
    ? product.available_heights
    : hasHeightVariants && product?.variants?.length > 0
      ? [...new Set(product.variants.map(v => v.height).filter(Boolean))]
      : [];

  const availableCompressionClasses = product?.available_compression_classes?.length > 0
    ? product.available_compression_classes
    : hasCompressionVariants && product?.variants?.length > 0
      ? [...new Set(product.variants.map(v => v.compression_class).filter(Boolean))]
      : [];

  // Derived state for filtering
  const filteredSizes = React.useMemo(() => {
    if (!hasSizeVariants) return [];
    if (!product.variants?.length) return product.available_sizes || [];
    
    let valid = product.variants;
    if (hasColorVariants && selectedColor) {
      valid = valid.filter(v => v.color === selectedColor);
    }
    
    const validSizes = new Set(valid.map(v => v.size).filter(Boolean));
    
    if (product.available_sizes?.length) {
      return product.available_sizes.filter(s => validSizes.has(s));
    }
    return Array.from(validSizes).sort();
  }, [product, hasSizeVariants, hasColorVariants, selectedColor]);

  const filteredSides = React.useMemo(() => {
    if (!hasSideVariants) return [];
    if (!product.variants?.length) return availableSides;
    
    let valid = product.variants;
    if (hasColorVariants && selectedColor) {
      valid = valid.filter(v => v.color === selectedColor);
    }
    if (hasSizeVariants && selectedSize) {
      valid = valid.filter(v => v.size === selectedSize);
    }
    
    const validSides = new Set(valid.map(v => v.side).filter(Boolean));
    
    if (availableSides.length) {
      return availableSides.filter(s => validSides.has(s));
    }
    return Array.from(validSides).sort();
  }, [product, hasSideVariants, hasColorVariants, selectedColor, hasSizeVariants, selectedSize, availableSides]);

  const filteredToeCaps = React.useMemo(() => {
    if (!hasToeCapVariants) return [];
    if (!product.variants?.length) return availableToeCaps;
    
    let valid = product.variants;
    if (hasCompressionVariants && selectedCompressionClass) valid = valid.filter(v => v.compression_class === selectedCompressionClass);
    if (hasSizeVariants && selectedSize) valid = valid.filter(v => v.size === selectedSize);
    if (hasHeightVariants && selectedHeight) valid = valid.filter(v => v.height === selectedHeight);
    if (hasColorVariants && selectedColor) valid = valid.filter(v => v.color === selectedColor);
    
    const validToeCaps = new Set(valid.map(v => v.toe_cap).filter(Boolean));
    return availableToeCaps.filter(tc => validToeCaps.has(tc));
  }, [product, hasToeCapVariants, availableToeCaps, selectedCompressionClass, selectedSize, selectedHeight, selectedColor]);

  const filteredHeights = React.useMemo(() => {
    if (!hasHeightVariants) return [];
    if (!product.variants?.length) return availableHeights;
    
    let valid = product.variants;
    if (hasCompressionVariants && selectedCompressionClass) valid = valid.filter(v => v.compression_class === selectedCompressionClass);
    if (hasSizeVariants && selectedSize) valid = valid.filter(v => v.size === selectedSize);
    if (hasToeCapVariants && selectedToeCap) valid = valid.filter(v => v.toe_cap === selectedToeCap);
    if (hasColorVariants && selectedColor) valid = valid.filter(v => v.color === selectedColor);
    
    const validHeights = new Set(valid.map(v => v.height).filter(Boolean));
    return availableHeights.filter(h => validHeights.has(h));
  }, [product, hasHeightVariants, availableHeights, selectedCompressionClass, selectedSize, selectedToeCap, selectedColor]);

  const filteredCompressionClasses = React.useMemo(() => {
    if (!hasCompressionVariants) return [];
    if (!product.variants?.length) return availableCompressionClasses;
    
    let valid = product.variants;
    if (hasSizeVariants && selectedSize) valid = valid.filter(v => v.size === selectedSize);
    if (hasHeightVariants && selectedHeight) valid = valid.filter(v => v.height === selectedHeight);
    if (hasToeCapVariants && selectedToeCap) valid = valid.filter(v => v.toe_cap === selectedToeCap);
    if (hasColorVariants && selectedColor) valid = valid.filter(v => v.color === selectedColor);
    
    const validClasses = new Set(valid.map(v => v.compression_class).filter(Boolean));
    return availableCompressionClasses.filter(cc => validClasses.has(cc));
  }, [product, hasCompressionVariants, availableCompressionClasses, selectedSize, selectedHeight, selectedToeCap, selectedColor]);

  // Reset invalid selections
  useEffect(() => {
    if (selectedSize && filteredSizes.length > 0 && !filteredSizes.includes(selectedSize)) setSelectedSize(null);
    setSizeConfirmed(false);
  }, [filteredSizes, selectedSize]);

  useEffect(() => {
    if (selectedSide && filteredSides.length > 0 && !filteredSides.includes(selectedSide)) setSelectedSide(null);
  }, [filteredSides, selectedSide]);

  useEffect(() => {
    if (selectedToeCap && filteredToeCaps.length > 0 && !filteredToeCaps.includes(selectedToeCap)) setSelectedToeCap(null);
  }, [filteredToeCaps, selectedToeCap]);

  useEffect(() => {
    if (selectedHeight && filteredHeights.length > 0 && !filteredHeights.includes(selectedHeight)) setSelectedHeight(null);
  }, [filteredHeights, selectedHeight]);

  useEffect(() => {
    if (selectedCompressionClass && filteredCompressionClasses.length > 0 && !filteredCompressionClasses.includes(selectedCompressionClass)) setSelectedCompressionClass(null);
  }, [filteredCompressionClasses, selectedCompressionClass]);

  // Validate measurements
  const validateMeasurement = (field, value) => {
    if (!value) return null;
    const numValue = parseFloat(value);
    
    if (field.min_value && numValue < parseFloat(field.min_value)) {
      return `Medida abaixo do mínimo (${field.min_value}${field.unit})`;
    }
    if (field.max_value && numValue > parseFloat(field.max_value)) {
      return `Medida acima do máximo (${field.max_value}${field.unit})`;
    }
    return null;
  };

  const hasOutOfRangeMeasurements = () => {
    if (!product?.requires_measurements || !product?.measurement_fields) return false;
    return product.measurement_fields.some(field => {
      const value = customMeasurements[field.label];
      if (!value) return false;
      return validateMeasurement(field, value) !== null;
    });
  };

  // Determine if selection is complete
  const isSelectionComplete = () => {
    // First check if there are any measurement errors
    if (hasOutOfRangeMeasurements()) return false;
    
    if (!hasVariants) {
      // Check custom measurements
      if (product?.requires_measurements && product?.measurement_fields?.length > 0) {
        const requiredFields = product.measurement_fields.filter(f => f.required !== false);
        for (const field of requiredFields) {
          if (!customMeasurements[field.label]) return false;
        }
      }
      return true;
    }
    if (hasColorVariants && !selectedColor) return false;
    if (hasSizeVariants && !selectedSize) return false;
    if (hasSizeVariants && !sizeConfirmed) return false;
    if (hasSideVariants && !selectedSide) return false;
    if (hasToeCapVariants && !selectedToeCap) return false;
    if (hasHeightVariants && !selectedHeight) return false;
    if (hasCompressionVariants && !selectedCompressionClass) return false;
    
    // Check custom measurements for variants too
    if (product?.requires_measurements && product?.measurement_fields?.length > 0) {
      const requiredFields = product.measurement_fields.filter(f => f.required !== false);
      for (const field of requiredFields) {
        if (!customMeasurements[field.label]) return false;
      }
    }
    return true;
  };

  // Find cart item considering variant
  const getCartKey = () => {
    if (!product) return null;
    if (!hasVariants) return product.id;
    return `${product.id}-${selectedColor || ''}-${selectedSize || ''}-${selectedSide || ''}-${selectedToeCap || ''}-${selectedHeight || ''}-${selectedCompressionClass || ''}`;
  };

  const inCart = product ? cart.find(item => item.cartKey === getCartKey() || (!hasVariants && item.id === product.id)) : null;
  
  // Sync quantity with cart
  useEffect(() => {
    if (inCart && isSelectionComplete()) {
      setQuantity(inCart.quantity);
    } else if (!inCart) {
      setQuantity(1);
    }
  }, [inCart, selectedColor, selectedSize, selectedSide, selectedToeCap, selectedHeight, selectedCompressionClass]);

  const images = product?.image_urls?.length > 0 ? product.image_urls : [];
  const hasMultipleImages = images.length > 1;

  // Get selected variant - memoized to update when selections change
  const selectedVariant = React.useMemo(() => {
    if (!hasVariants || !product?.variants?.length) return null;
    return product.variants.find(v => {
      if (product.variant_type === 'color') return v.color === selectedColor;
      if (product.variant_type === 'size') return v.size === selectedSize;
      if (product.variant_type === 'side') return v.side === selectedSide;
      if (product.variant_type === 'toe_cap') return v.toe_cap === selectedToeCap;
      if (product.variant_type === 'height') return v.height === selectedHeight;
      if (product.variant_type === 'compression') return v.compression_class === selectedCompressionClass;
      if (product.variant_type === 'color_size') return v.color === selectedColor && v.size === selectedSize;
      if (product.variant_type === 'size_side') return v.size === selectedSize && v.side === selectedSide;
      if (product.variant_type === 'color_side') return v.color === selectedColor && v.side === selectedSide;
      if (product.variant_type === 'color_size_side') return v.color === selectedColor && v.size === selectedSize && v.side === selectedSide;
      if (product.variant_type === 'compression_size_height_toe_cap_color') {
        return v.compression_class === selectedCompressionClass && 
               v.size === selectedSize && 
               v.height === selectedHeight && 
               v.toe_cap === selectedToeCap && 
               v.color === selectedColor;
      }
      return false;
    });
  }, [product, hasVariants, selectedColor, selectedSize, selectedSide, selectedToeCap, selectedHeight, selectedCompressionClass]);

  // Promo Logic - Recalculate when variant changes
  const { displayPrice, comparePrice, promoBadge, rewardProductItem, hasDiscount, discountPercent, applicableCoupon } = React.useMemo(() => {
    // Get base price from variant or product
    let basePrice = selectedVariant?.price || product?.price;
    let baseComparePrice = selectedVariant?.compare_price || product?.compare_price;
    
    const applicableCoupon = getBestApplicableCoupon(product, activeCoupons, brand);
    
    let promoBadge = null;
    let rewardProductItem = null;
    let finalDisplayPrice = basePrice;
    let finalComparePrice = baseComparePrice;

    if (applicableCoupon) {
        if (applicableCoupon.promotion_type === 'buy_x_get_y') {
            if (applicableCoupon.reward_product_ids && applicableCoupon.reward_product_ids.length > 0) {
                if (applicableCoupon.reward_product_ids.includes(product.id)) {
                    // This IS the reward product - do not show offer badge
                    promoBadge = null;
                    rewardProductItem = null;
                } else {
                    // This IS the trigger product
                    const rewardId = applicableCoupon.reward_product_ids[0];
                    rewardProductItem = allProducts.find(p => p.id === rewardId);
                    promoBadge = null; // We use rewardProductItem to render the specialized badge
                }
            } else {
                promoBadge = `Promoção: Leve ${applicableCoupon.buy_x_quantity + applicableCoupon.get_y_quantity} Pague ${applicableCoupon.buy_x_quantity}`;
            }
        } else if (applicableCoupon.promotion_type === 'tiered_discount') {
            const minTier = applicableCoupon.tier_config?.reduce((min, t) => t.min_qty < min ? t.min_qty : min, 999);
            if (minTier < 999) promoBadge = `Desconto disponível a partir de ${minTier} unidades`;
        } else if (applicableCoupon.discount_type === 'percentage_shop' || applicableCoupon.discount_type === 'percentage_item' || applicableCoupon.discount_type === 'percentage') {
            const discountVal = (basePrice * applicableCoupon.discount_value) / 100;
            finalDisplayPrice = basePrice - discountVal;
            if (!finalComparePrice) finalComparePrice = basePrice;
            promoBadge = `Promoção: -${applicableCoupon.discount_value}%`;
        }
    }

    const hasDiscount = finalComparePrice && finalComparePrice > finalDisplayPrice;
    const discountPercent = hasDiscount ? Math.round((1 - finalDisplayPrice / finalComparePrice) * 100) : 0;
    
    return {
      displayPrice: finalDisplayPrice,
      comparePrice: finalComparePrice,
      promoBadge,
      rewardProductItem,
      hasDiscount,
      discountPercent,
      applicableCoupon
    };
  }, [product, selectedVariant, activeCoupons, brand, allProducts]);

  // Get stock for current selection
  const getAvailableStock = () => {
    if (!hasVariants) return product?.stock_quantity || 0;
    // If product has variants but no detailed variants array, use product stock
    if (!product?.variants?.length) return product?.stock_quantity || 0;
    if (!selectedVariant) return 0;
    return selectedVariant.stock_quantity || 0;
  };

  const availableStock = getAvailableStock();

  // Check if can add to cart - allow backorder when stock is 0, unless prevented
  const canAddToCart = () => {
    if (!isSelectionComplete()) return false;
    const preventBackorder = product?.prevent_backorder || product?.condition === 'used';
    if (availableStock === 0 && preventBackorder) return false;
    return true; // Allow even if stock is 0 (backorder) if not prevented
  };

  // Check if this is a backorder
  const isBackorder = () => {
    if (!isSelectionComplete()) return false;
    return availableStock === 0;
  };

  // Get price (variant might have different price)
  const getPrice = () => {
    if (selectedVariant?.price) return selectedVariant.price;
    return product?.price;
  };

  const handleAddToCart = () => {
    if (!canAddToCart()) return;
    
    if (inCart && onUpdateCartItem) {
      // Update existing item
      const cartItem = {
        ...inCart,
        quantity: quantity,
        isBackorder: quantity > availableStock,
        customMeasurements: product?.requires_measurements ? customMeasurements : undefined
      };
      onUpdateCartItem(cartItem, quantity);
      toast.success("Carrinho atualizado!");
    } else {
      // Add new item
      const cartItem = {
        ...product,
        cartKey: getCartKey(),
        quantity: quantity,
        selectedColor: selectedColor,
        selectedSize: selectedSize,
        selectedSide: selectedSide,
        selectedToeCap: selectedToeCap,
        selectedHeight: selectedHeight,
        selectedCompressionClass: selectedCompressionClass,
        selectedVariant: selectedVariant,
        price: getPrice(),
        delivery_option: selectedVariant?.delivery_option || product.delivery_option,
        maxStock: availableStock || 99,
        isBackorder: quantity > availableStock,
        customMeasurements: product?.requires_measurements ? customMeasurements : undefined
      };
      
      onAddToCart(cartItem);
      toast.success("Adicionado ao carrinho!");
    }
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  if (!product) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      {/* Guide Zoom Overlay */}
      {isGuideZoomed && product?.measure_guide_image_url && (
        <div 
          className="fixed inset-0 z-[60] bg-black/90 flex flex-col items-center justify-center p-4"
          onClick={(e) => { e.stopPropagation(); setIsGuideZoomed(false); }}
        >
          <button 
            className="absolute top-4 right-4 p-2 text-white hover:bg-white/10 rounded-full"
            onClick={(e) => { e.stopPropagation(); setIsGuideZoomed(false); }}
          >
            <X className="w-8 h-8" />
          </button>
          
          <div className="relative w-full max-w-5xl h-full flex items-center justify-center">
            <img 
              src={product.measure_guide_image_url} 
              alt="Tabela de tamanhos" 
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* Image Zoom Overlay */}
      {isZoomed && (
        <div 
          className="fixed inset-0 z-[60] bg-black/90 flex flex-col items-center justify-center p-4"
          onClick={(e) => { e.stopPropagation(); setIsZoomed(false); }}
        >
          <button 
            className="absolute top-4 right-4 p-2 text-white hover:bg-white/10 rounded-full"
            onClick={(e) => { e.stopPropagation(); setIsZoomed(false); }}
          >
            <X className="w-8 h-8" />
          </button>
          
          <div className="relative w-full max-w-5xl h-full flex items-center justify-center">
            <img 
              src={images[currentImageIndex]} 
              alt={product.name} 
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            
            {hasMultipleImages && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); prevImage(); }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-colors backdrop-blur-sm"
                >
                  <ChevronLeft className="w-8 h-8" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); nextImage(); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-colors backdrop-blur-sm"
                >
                  <ChevronRight className="w-8 h-8" />
                </button>
              </>
            )}
          </div>
          
          {/* Thumbnails in Zoom */}
          {hasMultipleImages && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 p-2 bg-black/50 rounded-xl backdrop-blur-md overflow-x-auto max-w-[90vw]" onClick={(e) => e.stopPropagation()}>
              {images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentImageIndex(idx)}
                  className={`w-12 h-12 rounded-lg overflow-hidden border-2 transition-colors ${
                    idx === currentImageIndex ? 'border-white' : 'border-transparent hover:border-white/50'
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <DialogContent className="max-w-5xl w-[95vw] max-h-[90vh] overflow-y-auto p-0">
        <DialogHeader className="sr-only">
          <DialogTitle>{product.name}</DialogTitle>
        </DialogHeader>
        
        <div className="grid md:grid-cols-2 gap-0">
          {/* LEFT COLUMN - Images & Measure Guide */}
          <div className="relative bg-slate-50 flex flex-col border-r border-slate-100">
            {/* Main Image Area */}
            <div className="relative group cursor-zoom-in bg-white m-4 rounded-xl border border-slate-100 h-[300px] md:h-[400px] flex items-center justify-center" onClick={() => setIsZoomed(true)}>
              {images.length > 0 ? (
                <LazyImage
                  src={images[currentImageIndex]}
                  alt={product.name}
                  className="w-full h-full object-contain p-4"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Package className="w-24 h-24 text-slate-300" />
                </div>
              )}
              
              <div className="absolute inset-0 bg-black/5 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none rounded-xl">
                <div className="bg-white/90 p-2 rounded-full shadow-lg">
                  <ZoomIn className="w-6 h-6 text-slate-700" />
                </div>
              </div>

              {/* Image Navigation */}
              {hasMultipleImages && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); prevImage(); }}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-colors"
                  >
                    <ChevronLeft className="w-6 h-6 text-slate-700" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); nextImage(); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-colors"
                  >
                    <ChevronRight className="w-6 h-6 text-slate-700" />
                  </button>
                </>
              )}

              {/* Badges */}
              <div className="absolute top-4 left-4 flex flex-col gap-2 pointer-events-none">
                {product.condition === 'used' && (
                  <Badge className="bg-amber-500 text-white text-sm px-3 py-1 shadow-sm flex items-center gap-1">
                    <Package className="w-3 h-3" /> Usado
                  </Badge>
                )}
                {hasDiscount && (
                  <Badge className="bg-red-500 text-white text-sm px-3 py-1 shadow-sm">
                    -{discountPercent}%
                  </Badge>
                )}
                {promoBadge && !rewardProductItem && (
                  <Badge className="bg-purple-600 text-white text-sm px-3 py-1 shadow-sm flex items-center gap-1">
                    <Tag className="w-3 h-3" />
                    {applicableCoupon?.promotion_type !== 'simple_discount' ? 'Promoção' : `-${applicableCoupon.discount_value}%`}
                  </Badge>
                )}

{/* Low stock badge removed */}
              </div>

              {/* Brand Logo */}
              {brand?.logo_url && (
                <div className="absolute bottom-4 right-4 z-10 bg-white/90 backdrop-blur-sm rounded-lg p-2 shadow-sm">
                  <img 
                    src={brand.logo_url} 
                    alt={brand.name} 
                    className="h-12 w-auto max-w-[120px] object-contain mix-blend-multiply"
                  />
                </div>
              )}
            </div>

            {/* Thumbnail Navigation */}
            {hasMultipleImages && (
              <div className="flex gap-2 px-4 pb-4 overflow-x-auto">
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden border-2 transition-colors bg-white ${
                      idx === currentImageIndex ? 'border-[#076f77]' : 'border-transparent hover:border-slate-300'
                    }`}
                  >
                    <img src={img} alt={`${product.name} ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* Video Section */}
            {product.video_url && (
              <div className="px-4 pb-4">
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                  <div className="aspect-video w-full">
                    {product.video_url.includes('youtube.com') || product.video_url.includes('youtu.be') ? (
                      <iframe
                        src={product.video_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : product.video_url.includes('vimeo.com') ? (
                      <iframe
                        src={product.video_url.replace('vimeo.com/', 'player.vimeo.com/video/')}
                        className="w-full h-full"
                        allow="autoplay; fullscreen; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <video
                        src={product.video_url}
                        controls
                        className="w-full h-full object-contain bg-black"
                      >
                        O seu navegador não suporta vídeos.
                      </video>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Measure Guide Section - Styled as Card */}
            {product.measure_guide_active && (product.measure_guide_text || product.measure_guide_image_url) && (
              <div className="p-6 mt-auto bg-red-50 border-t border-red-100">
                <div className="flex items-center gap-2 mb-2 text-red-800">
                  <Ruler className="w-5 h-5" />
                  <h3 className="font-semibold text-lg">Tabela de tamanhos</h3>
                </div>

                <p className="text-xs font-bold text-red-600 mb-3 flex items-center gap-1 bg-red-100/50 p-2 rounded border border-red-200">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  Atenção: Verifique os tamanhos antes de encomendar
                </p>
                
                <div className="bg-white rounded-xl border border-red-200 p-2 shadow-sm overflow-hidden">
                  {product.measure_guide_image_url && (
                    <div 
                      className="w-full bg-white rounded-lg overflow-hidden cursor-zoom-in" 
                      onClick={() => setIsGuideZoomed(true)}
                    >
                      <img 
                        src={product.measure_guide_image_url} 
                        alt="Tabela de tamanhos" 
                        className="w-full h-auto object-contain" 
                      />
                    </div>
                  )}
                </div>
                {product.measure_guide_text && (
                    <p className="mt-3 text-sm text-slate-600 whitespace-pre-line leading-relaxed">{product.measure_guide_text}</p>
                )}
              </div>
            )}
          </div>

          {/* RIGHT COLUMN - Info & Actions */}
          <div className="p-6 relative bg-white">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-8 h-8 bg-slate-100 hover:bg-slate-200 rounded-full flex items-center justify-center transition-colors z-20"
            >
              <X className="w-4 h-4" />
            </button>

            {rewardProductItem && (
              <div 
                className="mb-4 bg-purple-50 border border-purple-200 rounded-lg p-3 flex items-center gap-3 cursor-pointer hover:bg-purple-100 transition-colors shadow-sm mr-10"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onViewDetails) onViewDetails(rewardProductItem);
                }}
              >
                <div className="w-12 h-12 bg-white rounded border border-purple-100 overflow-hidden shrink-0 flex items-center justify-center">
                  {rewardProductItem.image_urls?.[0] ? (
                    <img src={rewardProductItem.image_urls[0]} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Package className="w-6 h-6 text-purple-300" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-bold text-purple-700 uppercase mb-0.5">OFERTA DE:</p>
                  <p className="text-sm text-purple-900 truncate font-medium leading-tight">{rewardProductItem.name}</p>
                  {rewardProductItem.price && (
                     <p className="text-xs text-purple-500 line-through mt-0.5">{rewardProductItem.price.toFixed(2)}€</p>
                  )}
                </div>
                <ChevronRight className="w-4 h-4 text-purple-400 shrink-0" />
              </div>
            )}

            {product.category && (
              <p className="text-xs text-[#076f77] font-bold uppercase tracking-wide mb-1">
                {product.category}
              </p>
            )}

            {/* Title and Share */}
            <div className="flex items-start justify-between gap-3 mb-2">
              <h2 className="text-2xl font-bold text-slate-900 flex-1">{product.name}</h2>
              <button
                onClick={() => {
                  const shopUrl = window.location.origin + '/Shop?product=' + product.id;
                  const message = `Olá! Gostava de partilhar este produto:\n\n*${product.name}*\n\nVer aqui: ${shopUrl}`;
                  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
                  window.open(whatsappUrl, '_blank');
                }}
                className="flex items-center gap-2 px-3 py-2 text-sm bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors shrink-0"
              >
                <Share2 className="w-4 h-4" />
                Partilhar
              </button>
            </div>
            
            {/* Price Area */}
            <div className="mb-4">
              <div className="flex items-baseline gap-2 mb-1 flex-wrap">
                {product.price_on_request ? (
                  <span className="text-3xl font-bold text-[#076f77]">Sob Consulta</span>
                ) : (
                  <>
                    <span className="text-3xl font-bold text-[#076f77]">{displayPrice?.toFixed(2)}€</span>
                    <span className="text-xs text-slate-400">(IVA incluído)</span>
                    {hasDiscount && (
                      <span className="text-xl text-slate-400 line-through">{comparePrice?.toFixed(2)}€</span>
                    )}
                  </>
                )}
              </div>

              <KlarnaWidget price={displayPrice} className="mb-2" />
              
              <div className="flex items-center gap-2">
                 <div className="flex">
                   {[1, 2, 3, 4, 5].map((star) => (
                     <Star 
                       key={star} 
                       className={`w-4 h-4 ${star <= Math.round(product.average_rating || 0) ? 'fill-[#cebe75] text-[#cebe75]' : 'text-slate-300'}`} 
                     />
                   ))}
                 </div>
                 <span className="text-sm text-slate-500 font-medium">
                   {product.average_rating ? product.average_rating.toFixed(1) : '0.0'} ({product.reviews_count || 0} avaliações)
                 </span>
              </div>
            </div>



            {/* Availability Hint if not selected */}
            {!isSelectionComplete() && hasVariants && (
               <p className="text-sm text-slate-500 mb-4">Selecione as opções para continuar</p>
            )}

            {/* Description */}
            {product.description && (
              <div className="mb-6">
                <h3 className="font-semibold text-slate-900 mb-2">Descrição</h3>
                <p className="text-slate-600 leading-relaxed text-sm">{product.description}</p>
              </div>
            )}

            {/* SKU */}
            {product.sku && (
              <p className="text-xs text-slate-400 mb-6">REF: {product.sku}</p>
            )}

            {/* Reviews Section */}
            <div className="mb-6 pt-4 border-t border-slate-100">
               <div className="flex items-center justify-between mb-4">
                 <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                   <MessageSquare className="w-4 h-4" /> Avaliações ({reviews.length})
                 </h3>
                 <Button variant="outline" size="sm" onClick={() => setShowReviewForm(!showReviewForm)}>
                   Escrever Avaliação
                 </Button>
               </div>
               
               {/* Review Form */}
               {showReviewForm && (
                 <form onSubmit={handleSubmitReview} className="bg-slate-50 p-4 rounded-lg mb-4 border border-slate-200">
                   <div className="mb-3">
                     <label className="block text-sm font-medium text-slate-700 mb-1">Classificação</label>
                     <div className="flex gap-1">
                       {[1, 2, 3, 4, 5].map((star) => (
                         <button
                           key={star}
                           type="button"
                           onClick={() => setReviewRating(star)}
                           className="focus:outline-none transition-transform hover:scale-110"
                         >
                           <Star 
                             className={`w-6 h-6 ${star <= reviewRating ? 'fill-[#cebe75] text-[#cebe75]' : 'text-slate-300'}`} 
                           />
                         </button>
                       ))}
                     </div>
                   </div>
                   <div className="mb-3">
                     <label className="block text-sm font-medium text-slate-700 mb-1">Comentário</label>
                     <Textarea 
                       value={reviewComment}
                       onChange={(e) => setReviewComment(e.target.value)}
                       placeholder="Partilhe a sua experiência..."
                       required
                       className="bg-white"
                     />
                   </div>
                   <div className="flex justify-end gap-2">
                     <Button type="button" variant="ghost" size="sm" onClick={() => setShowReviewForm(false)}>Cancelar</Button>
                     <Button type="submit" size="sm" className="bg-[#076f77] hover:bg-[#065a61]">Enviar</Button>
                   </div>
                 </form>
               )}
               
               {reviews.length === 0 ? (
                 <p className="text-sm text-slate-500 italic">Ainda não existem avaliações para este produto.</p>
               ) : (
                 <div className="space-y-3 max-h-40 overflow-y-auto pr-1">
                   {reviews.map((review) => (
                     <div key={review.id} className="bg-slate-50 p-3 rounded-lg text-sm">
                       <div className="flex justify-between mb-1">
                         <span className="font-medium">{review.customer_name}</span>
                         <div className="flex"><Star className="w-3 h-3 fill-[#cebe75] text-[#cebe75]" /></div>
                       </div>
                       <p className="text-slate-600">{review.comment}</p>
                     </div>
                   ))}
                 </div>
               )}
            </div>
            


            {/* Variants & Actions Area */}
            <div className="mt-6 pt-6 border-t border-slate-100">
                {hasVariants && (
                  <div className="space-y-4 mb-6">
                    {/* 1. Compression Class (First) */}
                    {hasCompressionVariants && filteredCompressionClasses.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Classe de Compressão: {selectedCompressionClass && <span className="text-[#076f77] font-semibold">{selectedCompressionClass}</span>}
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {filteredCompressionClasses.map((cc) => (
                            <button
                              key={cc}
                              onClick={() => setSelectedCompressionClass(cc)}
                              className={`h-10 px-4 rounded-lg border text-sm font-medium transition-all ${selectedCompressionClass === cc ? 'border-[#076f77] bg-[#076f77] text-white' : 'border-slate-300 bg-white hover:border-[#076f77]'}`}
                            >
                              {cc}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 2. Custom Measurements (After compression class, before size) */}
                    {product?.requires_measurements && product?.measurement_fields?.length > 0 && (!hasCompressionVariants || selectedCompressionClass) && (
                      <div className="bg-orange-50 p-3 rounded-lg border border-orange-300">
                        <div className="flex items-center gap-1.5 mb-2">
                          <Ruler className="w-4 h-4 text-orange-600" />
                          <h3 className="text-sm font-semibold text-orange-900">Medidas Personalizadas</h3>
                        </div>
                        <p className="text-xs text-orange-700 mb-2">
                          ⚠️ Preencha todos os campos obrigatórios:
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          {product.measurement_fields.map((field, idx) => {
                            const error = validateMeasurement(field, customMeasurements[field.label]);
                            return (
                              <div key={idx}>
                                <Label className="text-xs font-medium text-slate-900 mb-1 block">
                                  {field.label} ({field.unit || 'cm'}) {field.required !== false && <span className="text-red-500">*</span>}
                                  {field.min_value && field.max_value && (
                                    <span className="text-[10px] text-slate-500 ml-1">
                                      ({field.min_value}-{field.max_value})
                                    </span>
                                  )}
                                </Label>
                                <Input
                                  type="number"
                                  step="0.1"
                                  placeholder={field.unit || 'cm'}
                                  value={customMeasurements[field.label] || ''}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setCustomMeasurements(prev => ({
                                      ...prev,
                                      [field.label]: value
                                    }));
                                    const error = validateMeasurement(field, value);
                                    setMeasurementErrors(prev => ({
                                      ...prev,
                                      [field.label]: error
                                    }));
                                  }}
                                  className={`bg-white h-9 text-sm ${
                                    error ? 'border-red-400 focus:border-red-400' : 'border-orange-200 focus:border-orange-400'
                                  }`}
                                  required={field.required !== false}
                                />
                                {error && (
                                  <p className="text-[10px] text-red-600 mt-0.5">{error}</p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        {hasOutOfRangeMeasurements() && (
                          <div className="mt-3 p-2 bg-red-100 border-2 border-red-400 rounded text-xs text-red-800 font-semibold">
                            🚫 <strong>Não é possível avançar:</strong> As medidas têm de estar dentro dos valores aceitáveis. Por favor ajuste as medidas ou contacte a loja.
                          </div>
                        )}
                      </div>
                    )}

                    {/* 3. Size (After measurements) */}
                    {hasSizeVariants && filteredSizes.length > 0 && (!hasCompressionVariants || selectedCompressionClass) && (!product?.requires_measurements || (product.measurement_fields?.every(f => f.required === false || customMeasurements[f.label]))) && (
                      <div className="bg-slate-50 p-4 rounded-lg border border-slate-100">
                        <div className="flex items-center justify-between mb-3">
                          <label className="block text-sm font-bold text-slate-900">
                            Tamanho: {selectedSize && <span className="text-[#076f77] font-normal">{selectedSize}</span>}
                          </label>
                          {product.measure_guide_active && (
                            <button 
                              onClick={() => setIsGuideZoomed(true)}
                              className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1 font-medium"
                            >
                              <Ruler className="w-4 h-4" />
                              Tabela de tamanhos
                            </button>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {filteredSizes.map((size) => (
                            <button
                              key={size}
                              onClick={() => setSelectedSize(size)}
                              className={`min-w-[48px] h-12 px-3 rounded-lg border text-base font-medium transition-all flex items-center justify-center gap-2 ${
                                selectedSize === size
                                  ? 'border-[#076f77] bg-white ring-1 ring-[#076f77] text-[#076f77]'
                                  : 'border-slate-300 bg-white hover:border-[#076f77] text-slate-700'
                              }`}
                            >
                              {size}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 4. Height (After size) */}
                    {hasHeightVariants && filteredHeights.length > 0 && (!hasSizeVariants || selectedSize) && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Altura: {selectedHeight && <span className="text-[#076f77] font-semibold">{selectedHeight}</span>}
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {filteredHeights.map((h) => (
                            <button
                              key={h}
                              onClick={() => setSelectedHeight(h)}
                              className={`h-10 px-4 rounded-lg border text-sm font-medium transition-all ${selectedHeight === h ? 'border-[#076f77] bg-[#076f77] text-white' : 'border-slate-300 bg-white hover:border-[#076f77]'}`}
                            >
                              {h}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 5. Toe Cap (After height) */}
                    {hasToeCapVariants && filteredToeCaps.length > 0 && (!hasHeightVariants || selectedHeight) && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Biqueira: {selectedToeCap && <span className="text-[#076f77] font-semibold">{selectedToeCap}</span>}
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {filteredToeCaps.map((tc) => (
                            <button
                              key={tc}
                              onClick={() => setSelectedToeCap(tc)}
                              className={`h-10 px-4 rounded-lg border text-sm font-medium transition-all ${selectedToeCap === tc ? 'border-[#076f77] bg-[#076f77] text-white' : 'border-slate-300 bg-white hover:border-[#076f77]'}`}
                            >
                              {tc}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 6. Color (After toe cap) */}
                    {hasColorVariants && product.available_colors?.length > 0 && (!hasToeCapVariants || selectedToeCap) && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Cor: {selectedColor && <span className="text-[#076f77] font-semibold">{selectedColor}</span>}
                        </label>
                        <div className="flex flex-wrap gap-3">
                          {product.available_colors.map((color) => {
                            const colorVariant = product.variants?.find(v => v.color === color.name);
                            const colorImage = color.image_url || colorVariant?.image_url;
                            return (
                              <button
                                key={color.name}
                                onClick={() => setSelectedColor(color.name)}
                                className={`flex flex-col items-center gap-1 transition-all ${selectedColor === color.name ? 'opacity-100' : 'opacity-70 hover:opacity-100'}`}
                              >
                                <div className={`w-12 h-12 rounded-md overflow-hidden border-2 transition-all ${selectedColor === color.name ? 'border-[#076f77] ring-2 ring-[#076f77]/30' : 'border-slate-200'}`}>
                                  {colorImage ? <img src={colorImage} alt={color.name} className="w-full h-full object-cover" /> : <div className="w-full h-full" style={{ backgroundColor: color.hex || '#ccc' }} />}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Side Selector (for other variant types) */}
                    {hasSideVariants && filteredSides.length > 0 && (!hasColorVariants || selectedColor) && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Lado: {selectedSide && <span className="text-[#076f77] font-semibold">{selectedSide}</span>}
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {filteredSides.map((side) => (
                            <button
                              key={side}
                              onClick={() => setSelectedSide(side)}
                              className={`h-10 px-4 rounded-lg border text-sm font-medium transition-all ${selectedSide === side ? 'border-[#076f77] bg-[#076f77] text-white' : 'border-slate-300 bg-white hover:border-[#076f77]'}`}
                            >
                              {side}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Custom Measurements for products WITHOUT variants */}
                {!hasVariants && product?.requires_measurements && product?.measurement_fields?.length > 0 && (
                  <div className="mb-6 bg-orange-50 p-3 rounded-lg border border-orange-300">
                    <div className="flex items-center gap-1.5 mb-2">
                      <Ruler className="w-4 h-4 text-orange-600" />
                      <h3 className="text-sm font-semibold text-orange-900">Medidas Personalizadas</h3>
                    </div>
                    <p className="text-xs text-orange-700 mb-2">
                      ⚠️ Preencha todos os campos obrigatórios:
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {product.measurement_fields.map((field, idx) => {
                        const error = validateMeasurement(field, customMeasurements[field.label]);
                        return (
                          <div key={idx}>
                            <Label className="text-xs font-medium text-slate-900 mb-1 block">
                              {field.label} ({field.unit || 'cm'}) {field.required !== false && <span className="text-red-500">*</span>}
                              {field.min_value && field.max_value && (
                                <span className="text-[10px] text-slate-500 ml-1">
                                  ({field.min_value}-{field.max_value})
                                </span>
                              )}
                            </Label>
                            <Input
                              type="number"
                              step="0.1"
                              placeholder={field.unit || 'cm'}
                              value={customMeasurements[field.label] || ''}
                              onChange={(e) => {
                                const value = e.target.value;
                                setCustomMeasurements(prev => ({
                                  ...prev,
                                  [field.label]: value
                                }));
                                const error = validateMeasurement(field, value);
                                setMeasurementErrors(prev => ({
                                  ...prev,
                                  [field.label]: error
                                }));
                              }}
                              className={`bg-white h-9 text-sm ${
                                error ? 'border-red-400 focus:border-red-400' : 'border-orange-200 focus:border-orange-400'
                              }`}
                              required={field.required !== false}
                            />
                            {error && (
                              <p className="text-[10px] text-red-600 mt-0.5">{error}</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {hasOutOfRangeMeasurements() && (
                      <div className="mt-3 p-2 bg-red-100 border-2 border-red-400 rounded text-xs text-red-800 font-semibold">
                        🚫 <strong>Não é possível avançar:</strong> As medidas têm de estar dentro dos valores aceitáveis. Por favor ajuste as medidas ou contacte a loja.
                      </div>
                    )}
                  </div>
                )}

                {/* Size Confirmation Checkbox (After measurements if they exist, otherwise after size selection) */}
                {hasSizeVariants && selectedSize && (
                  (!product?.requires_measurements || (product?.requires_measurements && (!hasVariants || ((!hasCompressionVariants || selectedCompressionClass) && (!hasSizeVariants || selectedSize) && (!hasHeightVariants || selectedHeight) && (!hasToeCapVariants || selectedToeCap) && (!hasColorVariants || selectedColor))))) && (
                    <div className="mb-6">
                      <div className="flex items-center space-x-2 bg-slate-50 p-3 rounded-lg border border-slate-200">
                        <Checkbox 
                          id="size-confirm-modal" 
                          checked={sizeConfirmed}
                          onCheckedChange={setSizeConfirmed}
                          className="border-slate-400 data-[state=checked]:bg-[#076f77] data-[state=checked]:border-[#076f77]"
                        />
                        <Label 
                          htmlFor="size-confirm-modal" 
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                        >
                          Confirmo que verifiquei as medidas / Tamanho
                        </Label>
                      </div>
                    </div>
                  )
                )}

                {/* Delivery Info - Always visible above button */}
                {isSelectionComplete() && (
                  <div className="flex items-center gap-2 mb-4 text-[#076f77] font-medium bg-[#076f77]/5 p-3 rounded-lg border border-[#076f77]/10">
                    <Truck className="w-5 h-5" />
                    <span>
                      {(() => {
                        let deliveryOption = product.delivery_option;
                        
                        if (hasVariants && selectedVariant?.delivery_option) {
                          deliveryOption = selectedVariant.delivery_option;
                        }
                        
                        return deliveryOption === '72h' ? 'Entrega em 72 horas' :
                               deliveryOption === '5days' ? 'Entrega em 5 dias úteis' :
                               deliveryOption === '15days' ? 'Entrega em 15 dias úteis' :
                               'Entrega em 72 horas';
                      })()}
                    </span>
                  </div>
                )}

                {/* Add Button */}
                <Button
                  size="lg"
                  className={`w-full text-lg py-6 font-bold shadow-sm ${
                    inCart ? 'bg-blue-600 hover:bg-blue-700 text-white' : 
                    !isSelectionComplete() ? 'bg-[#e6e1c5] hover:bg-[#dcd6b0] text-[#076f77]' : 
                    (availableStock === 0 && (product.prevent_backorder || product.condition === 'used')) ? 'bg-slate-200 text-slate-400 cursor-not-allowed' :
                    'bg-[#076f77] hover:bg-[#065a61] text-white'
                  }`}
                  onClick={handleAddToCart}
                  disabled={!canAddToCart()}
                >
                    {!isSelectionComplete() ? (
                        <>
                            <Package className="w-5 h-5 mr-2 opacity-50" />
                            Selecione opções
                        </>
                    ) : inCart ? (
                        <>
                            <ShoppingCart className="w-5 h-5 mr-2" />
                            Atualizar Carrinho ({quantity} unid.)
                        </>
                    ) : availableStock === 0 ? (
                         <>
                            <ShoppingCart className="w-5 h-5 mr-2" />
                            Comprar
                        </>
                    ) : (
                        <>
                            <ShoppingCart className="w-5 h-5 mr-2" />
                            Comprar
                        </>
                    )}
                </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}